import { createSignal, createResource, createEffect, createMemo, For, Show } from 'solid-js';

// Absensi — fingerprint machines and the RAW scan log they produce.
// This is the upstream layer: machines push finger scans into tm_attendance.
// (The interpretation into daily attendance → payroll inputs is a later
// milestone; this page is monitoring + machine management.)

async function getJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error('HTTP ' + res.status);
  return res.json();
}

async function postJSON(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
}

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const TIPE_BADGE = {
  Masuk: 'success', Pulang: 'info',
  IstirahatMulai: 'warning', IstirahatSelesai: 'warning',
  LemburMulai: 'warning', LemburSelesai: 'warning',
};
const MACHINE_TYPES = ['ALL', 'Masuk', 'Pulang', 'IstirahatMulai', 'IstirahatSelesai', 'LemburMulai', 'LemburSelesai'];

export default function Absensi() {
  const [tab, setTab] = createSignal('mesin');
  const [msg, setMsg] = createSignal(null);

  return (
    <>
      <div class="page-head">
        <h2>Absensi</h2>
        <div class="spacer" />
        <div class="seg">
          <button class="seg-item" classList={{ active: tab() === 'mesin' }} onClick={() => { setTab('mesin'); setMsg(null); }}>Mesin</button>
          <button class="seg-item" classList={{ active: tab() === 'mentah' }} onClick={() => { setTab('mentah'); setMsg(null); }}>Data Mentah</button>
          <button class="seg-item" classList={{ active: tab() === 'libur' }} onClick={() => { setTab('libur'); setMsg(null); }}>Hari Libur</button>
        </div>
      </div>

      <Show when={msg()}>
        <div class={`kg-verdict ${msg().ok ? 'ok' : 'fail'}`} style="margin-bottom:12px">{msg().msg}</div>
      </Show>

      <Show when={tab() === 'mesin'}><MachinesTab setMsg={setMsg} /></Show>
      <Show when={tab() === 'mentah'}><RawTab /></Show>
      <Show when={tab() === 'libur'}><HolidaysTab setMsg={setMsg} /></Show>
    </>
  );
}

// ── Hari Libur: per-company holiday calendar ───────────────────────────
const WEEKDAYS = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
const weekdayOf = (date) => WEEKDAYS[new Date(date + 'T00:00:00').getDay()];

function HolidaysTab(props) {
  const nowYear = new Date().getFullYear();
  const [year, setYear] = createSignal(nowYear);
  const [list, { refetch }] = createResource(year, (y) => getJSON(`/api/holidays?year=${y}`));
  const [edit, setEdit] = createSignal(null); // {date, description, kind, isNew}

  async function save() {
    const r = await postJSON('/api/holiday',
      { date: edit().date, description: edit().description, kind: edit().kind });
    if (r.ok) { props.setMsg({ ok: true, msg: `Hari libur ${edit().date} tersimpan.` }); setEdit(null); refetch(); }
    else props.setMsg({ ok: false, msg: r.data.error });
  }
  async function del(h) {
    if (!confirm(`Hapus hari libur ${h.date}${h.description ? ` (${h.description})` : ''}?`)) return;
    const res = await fetch(`/api/holiday?date=${encodeURIComponent(h.date)}`, { method: 'DELETE' });
    if (res.ok) { props.setMsg({ ok: true, msg: `Hari libur ${h.date} dihapus.` }); refetch(); }
    else { const d = await res.json().catch(() => ({})); props.setMsg({ ok: false, msg: d.error }); }
  }

  return (
    <div class="card card-pad vg-card ab-card">
      <p class="ky-vars-note">
        Kalender hari libur perusahaan ini. Hari libur (dan hari Minggu) dihitung
        sebagai hari LIBUR berbayar dalam perhitungan absensi — perbarui setiap tahun.
        Tanggal adalah kuncinya; untuk memindah tanggal, hapus lalu tambah lagi.
      </p>
      <div class="ky-vars-head">
        <h4 class="kg-hist-title">Hari Libur</h4>
        <label class="kg-field" style="margin-left:14px">
          <span>Tahun</span>
          <select class="kg-select" value={year()} onInput={(e) => setYear(Number(e.currentTarget.value))}>
            <For each={[nowYear - 2, nowYear - 1, nowYear, nowYear + 1]}>{(y) => <option value={y}>{y}</option>}</For>
          </select>
        </label>
        <div class="spacer" />
        <button class="btn ghost" onClick={() => setEdit({ date: today(), description: '', kind: 'nasional', isNew: true })}>+ hari libur</button>
      </div>

      <Show when={edit()}>
        <div class="pa-company-form">
          <div class="pa-company-title">{edit().isNew ? 'Hari libur baru' : 'Ubah hari libur'}</div>
          <div class="pa-company-grid">
            <label class="kg-field"><span>Tanggal</span>
              <input type="date" value={edit().date} readonly={!edit().isNew}
                onInput={(e) => setEdit({ ...edit(), date: e.currentTarget.value })} /></label>
            <label class="kg-field"><span>Keterangan</span>
              <input type="text" placeholder="mis. Hari Raya Idul Fitri" value={edit().description ?? ''}
                onInput={(e) => setEdit({ ...edit(), description: e.currentTarget.value })} /></label>
            <label class="kg-field"><span>Jenis</span>
              <select class="kg-select" value={edit().kind} onInput={(e) => setEdit({ ...edit(), kind: e.currentTarget.value })}>
                <option value="nasional">nasional</option>
                <option value="internal">internal</option>
              </select></label>
          </div>
          <div class="pa-company-row">
            <button class="btn primary pa-company-save" onClick={save}>Simpan</button>
            <button class="btn ghost" onClick={() => setEdit(null)}>Batal</button>
          </div>
        </div>
      </Show>

      <table class="data">
        <thead><tr><th>Tanggal</th><th>Hari</th><th>Keterangan</th><th>Jenis</th><th></th></tr></thead>
        <tbody>
          <For each={list() ?? []}>{(h) =>
            <tr>
              <td class="ky-nik">{h.date}</td>
              <td>{weekdayOf(h.date)}</td>
              <td>{h.description || '—'}</td>
              <td><span class={`badge ${h.kind === 'internal' ? 'warning' : 'info'}`}>{h.kind}</span></td>
              <td class="num"><span class="org-actions">
                <button class="btn ghost" onClick={() => setEdit({ ...h, isNew: false })}>ubah</button>
                <button class="btn ghost" onClick={() => del(h)}>hapus</button>
              </span></td>
            </tr>
          }</For>
        </tbody>
      </table>
      <Show when={list() && list().length === 0}>
        <p class="kg-meta-text">Belum ada hari libur untuk tahun {year()}.</p>
      </Show>
    </div>
  );
}

// ── Mesin: fingerprint machine master ──────────────────────────────────
function MachinesTab(props) {
  const [list, { refetch }] = createResource(() => getJSON('/api/machines'));
  const [edit, setEdit] = createSignal(null);

  async function save() {
    const r = await postJSON('/api/machines', edit());
    if (r.ok) {
      props.setMsg({ ok: true, msg: `Mesin ${edit().serial} tersimpan.` });
      setEdit(null);
      refetch();
    } else props.setMsg({ ok: false, msg: r.data.error });
  }

  return (
    <div class="card card-pad vg-card ab-card">
      <p class="ky-vars-note">
        Mesin sidik jari milik perusahaan ini. Setiap scan jari masuk ke tabel
        mentah (Data Mentah) dengan nomor seri mesin sebagai kuncinya.
      </p>
      <div class="ky-vars-head">
        <h4 class="kg-hist-title">Mesin</h4>
        <div class="spacer" />
        <button class="btn ghost" onClick={() => setEdit({ serial: '', name: '', type: 'ALL', ipLocal: '', active: true, note: '', isNew: true })}>
          + mesin
        </button>
      </div>

      <Show when={edit()}>
        <div class="pa-company-form">
          <div class="pa-company-title">{edit().isNew ? 'Mesin baru' : `Ubah mesin`}</div>
          <div class="pa-company-grid">
            <label class="kg-field">
              <span>Nomor seri</span>
              <input type="text" placeholder="mis. Fio66208023190137" value={edit().serial ?? ''}
                title={edit().isNew ? undefined : 'Harus PERSIS sama dengan yang dikirim mesin (lihat SNMesin di Data Mentah) — mengubahnya memindahkan pencocokan scan ke nomor baru'}
                onInput={(e) => setEdit({ ...edit(), serial: e.currentTarget.value })} />
            </label>
            <label class="kg-field">
              <span>Nama mesin</span>
              <input type="text" placeholder="mis. Fingerspot REVO" value={edit().name ?? ''}
                onInput={(e) => setEdit({ ...edit(), name: e.currentTarget.value })} />
            </label>
            <label class="kg-field">
              <span>Tipe scan</span>
              <select value={edit().type} onInput={(e) => setEdit({ ...edit(), type: e.currentTarget.value })}>
                <For each={MACHINE_TYPES}>{(t) => <option value={t}>{t}</option>}</For>
              </select>
            </label>
            <label class="kg-field">
              <span>IP lokal</span>
              <input type="text" placeholder="mis. 192.168.1.1" value={edit().ipLocal ?? ''}
                onInput={(e) => setEdit({ ...edit(), ipLocal: e.currentTarget.value })} />
            </label>
          </div>
          <div class="pa-company-row">
            <label class="ky-check">
              <input type="checkbox" checked={edit().active}
                onInput={(e) => setEdit({ ...edit(), active: e.currentTarget.checked })} />
              aktif
            </label>
            <button class="btn primary pa-company-save" onClick={save}>Simpan</button>
            <button class="btn ghost" onClick={() => setEdit(null)}>Batal</button>
          </div>
        </div>
      </Show>

      <table class="data">
        <thead>
          <tr><th>Nomor Seri</th><th>Nama</th><th>Tipe</th><th>IP Lokal</th><th class="num">Scan</th><th>Scan Terakhir</th><th>Status</th><th></th></tr>
        </thead>
        <tbody>
          <For each={list() ?? []}>{(m) =>
            <tr classList={{ inactive: !m.active }}>
              <td class="ky-nik">{m.serial}</td>
              <td>{m.name || '—'}</td>
              <td>{m.type}</td>
              <td class="ky-nik">{m.ipLocal || '—'}</td>
              <td class="num">{m.scans.toLocaleString('id-ID')}</td>
              <td class="kg-meta-text">{m.lastScan || '—'}</td>
              <td><span class={`badge ${m.active ? 'success' : 'danger'}`}>{m.active ? 'aktif' : 'nonaktif'}</span></td>
              <td class="num"><button class="btn ghost" onClick={() => setEdit({ ...m, isNew: false, origSerial: m.serial })}>ubah</button></td>
            </tr>
          }</For>
        </tbody>
      </table>
      <Show when={list() && list().length === 0}>
        <p class="kg-meta-text">Belum ada mesin terdaftar untuk perusahaan ini.</p>
      </Show>
    </div>
  );
}

// ── Data Mentah: raw fingerprint scans ─────────────────────────────────
function RawTab() {
  const [from, setFrom] = createSignal(today());
  const [to, setTo] = createSignal(today());
  const [search, setSearch] = createSignal('');
  const [serial, setSerial] = createSignal('');
  const [dept, setDept] = createSignal('');

  const [machines] = createResource(() => getJSON('/api/machines'));
  const [departments] = createResource(() => getJSON('/api/departments'));

  // Shared filter params for the grid and the export (the export omits limit).
  const baseParams = () => {
    const p = new URLSearchParams();
    if (from()) p.set('from', from());
    if (to()) p.set('to', to());
    if (search().trim()) p.set('q', search().trim());
    if (dept()) p.set('dept', dept());
    if (serial()) p.set('serial', serial());
    return p;
  };
  const params = createMemo(() => {
    const p = baseParams();
    p.set('limit', '300');
    return p.toString();
  });

  const [data] = createResource(params, (p) => getJSON(`/api/attendance?${p}`));
  const [exporting, setExporting] = createSignal(false);

  // Export the CURRENT filter (no row limit) as .xlsx. We fetch (so the patched
  // window.fetch attaches X-Company-ID for the active company) and download the
  // returned blob — a plain anchor navigation would miss that header.
  async function exportExcel() {
    const p = baseParams();
    setExporting(true);
    try {
      const res = await fetch(`/api/attendance/export?${p.toString()}`);
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        alert(e.error || 'Gagal mengekspor data.');
        return;
      }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = m ? m[1] : 'data-mesin.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Gagal mengekspor data: ' + err.message);
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <div class="card card-pad ab-filter">
        <label class="kg-field"><span>Dari</span>
          <input type="date" value={from()} onInput={(e) => setFrom(e.currentTarget.value)} /></label>
        <label class="kg-field"><span>Sampai</span>
          <input type="date" value={to()} onInput={(e) => setTo(e.currentTarget.value)} /></label>
        <label class="kg-field"><span>Mesin</span>
          <select class="kg-select" value={serial()} onInput={(e) => setSerial(e.currentTarget.value)}>
            <option value="">Semua mesin</option>
            <For each={machines() ?? []}>{(m) => <option value={m.serial}>{m.name || m.serial}</option>}</For>
          </select></label>
        <label class="kg-field"><span>Departemen</span>
          <select class="kg-select" value={dept()} onInput={(e) => setDept(e.currentTarget.value)}>
            <option value="">Semua departemen</option>
            <For each={departments() ?? []}>{(d) => <option value={d.code}>{d.name}</option>}</For>
          </select></label>
        <label class="kg-field"><span>Cari</span>
          <input type="text" placeholder="FID, nama, atau NIK" value={search()} onInput={(e) => setSearch(e.currentTarget.value)} /></label>
        <button class="btn primary ab-export" disabled={exporting()} onClick={exportExcel}>
          {exporting() ? 'Menyiapkan…' : '⬇ Export Excel'}
        </button>
      </div>

      <Show when={data()}>
        <div class="ab-summary kg-meta-text">
          Menampilkan {data().shown.toLocaleString('id-ID')} dari {data().total.toLocaleString('id-ID')} scan
          <Show when={data().total > data().shown}> (terbaru dulu; persempit filter untuk melihat lainnya)</Show>
        </div>
        <div class="card ab-raw">
          <table class="data">
            <thead>
              <tr><th>Waktu Scan</th><th>FID</th><th>Karyawan</th><th>Tipe</th><th>Mesin</th><th class="num">Mode/Kode</th></tr>
            </thead>
            <tbody>
              <For each={data().scans}>{(s) =>
                <tr>
                  <td class="ky-nik">{s.waktuScan}</td>
                  <td class="ky-nik">{s.fid}</td>
                  <td>
                    <Show when={s.nik} fallback={<span class="vg-dim">tidak dikenal</span>}>
                      <b>{s.nik}</b> {s.name}
                    </Show>
                  </td>
                  <td><Show when={s.tipeAbsen} fallback={<span class="vg-dim">—</span>}>
                    <span class={`badge ${TIPE_BADGE[s.tipeAbsen] ?? 'info'}`}>{s.tipeAbsen}</span>
                  </Show></td>
                  <td>
                    {s.namaMesin || s.snMesin}
                    <Show when={s.foreignMachine}>
                      {' '}<span class="badge info" title="Scan dari mesin perusahaan lain — karyawan ini terdaftar sidik jari di sana (Mesin sidik jari (perusahaan) pada master karyawan)">mesin lain</span>
                    </Show>
                  </td>
                  <td class="num kg-meta-text">{s.modeAbsen}/{s.kodeAbsen}</td>
                </tr>
              }</For>
            </tbody>
          </table>
          <Show when={data().scans.length === 0}>
            <p class="kg-meta-text" style="padding:14px">Tidak ada scan pada rentang/filter ini.</p>
          </Show>
        </div>
      </Show>
    </>
  );
}
